import { computed } from 'vue';
import { defineAnnotationToolStore } from '@/src/utils/defineAnnotationToolStore';
import type { Vector3 } from '@kitware/vtk.js/types';
import { Manifest, StateFile } from '@/src/io/state-file/schema';
import { RECTANGLE_LABEL_DEFAULTS } from '@/src/config';
import { ToolID } from '@/src/types/annotation-tool';
import { getPlaneTransforms } from '@/src/utils/frameOfReference';

import { useAnnotationTool } from './useAnnotationTool';

const rectangleDefaults = () => ({
  firstPoint: [0, 0, 0] as Vector3,
  secondPoint: [0, 0, 0] as Vector3,
  id: '' as ToolID,
  name: 'Rectangle',
  fillColor: 'transparent',
});

const newLabelDefault = {
  fillColor: 'transparent',
};

export const useRectangleStore = defineAnnotationToolStore('rectangles', () => {
  const toolAPI = useAnnotationTool({
    toolDefaults: rectangleDefaults,
    initialLabels: RECTANGLE_LABEL_DEFAULTS,
    newLabelDefault,
  });

  function getPoints(id: ToolID) {
    const tool = toolAPI.toolByID.value[id];
    return [tool.firstPoint, tool.secondPoint];
  }

  // Area (mm^2) spanned by the two corner points within the tool's plane.
  const areaByID = computed<Record<string, number>>(() => {
    const byID = toolAPI.toolByID.value;
    return toolAPI.toolIDs.value.reduce((areas, id) => {
      const tool = byID[id];
      const { to2D } = getPlaneTransforms(tool.frameOfReference);
      const [x1, y1] = to2D(tool.firstPoint);
      const [x2, y2] = to2D(tool.secondPoint);
      return Object.assign(areas, {
        [id]: Math.abs((x2 - x1) * (y2 - y1)),
      });
    }, {});
  });

  // --- serialization --- //

  function serialize(state: StateFile) {
    if (!state.manifest.tools) return;
    state.manifest.tools.rectangles = toolAPI.serializeTools();
  }

  function deserialize(manifest: Manifest, dataIDMap: Record<string, string>) {
    toolAPI.deserializeTools(manifest.tools?.rectangles, dataIDMap);
  }

  return {
    ...toolAPI,
    getPoints,
    areaByID,
    serialize,
    deserialize,
  };
});
